import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import { addMonths, localISODate, monthGrid, monthTitle } from "@/lib/date-range";
import { fmtDate } from "@/lib/format";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

/**
 * Модальный календарь-месяц — общая основа выбора дат (`DateRangePicker`,
 * `DateCalendarDialog`). Свой, без зависимостей: готового date-picker в проекте
 * нет, а нативный `<input type=date>` на ТСД Urovo неудобен.
 *
 * Что выбрано и что делать по тапу — решает обёртка; здесь только сетка,
 * листание месяцев, подсветка сегодняшнего дня и закрытие по «назад»/скриму.
 */
export function CalendarDialog({
  initialMonthOf,
  isSelected,
  isInRange,
  onDay,
  onClose,
  hint,
}: {
  /** Дата `yyyy-mm-dd`, чей месяц показать первым. */
  initialMonthOf: string;
  isSelected: (iso: string) => boolean;
  isInRange?: (iso: string) => boolean;
  onDay: (iso: string) => void;
  onClose: () => void;
  hint: React.ReactNode;
}): React.ReactElement {
  // Аппаратная «назад» закрывает календарь, а не уводит со страницы.
  useBackGuard(true, onClose);
  const [view, setView] = useState(() => {
    const [y, m] = initialMonthOf.split("-").map(Number);
    return { year: y || new Date().getFullYear(), month0: (m || 1) - 1 };
  });
  const today = localISODate();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        className="w-full max-w-sm rounded-2xl bg-white p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setView(addMonths(view.year, view.month0, -1))}
            className="flex size-10 items-center justify-center rounded-md hover:bg-accent"
            aria-label="Предыдущий месяц"
          >
            <ChevronLeft className="size-5" />
          </button>
          <span className="text-sm font-semibold">{monthTitle(view.year, view.month0)}</span>
          <button
            type="button"
            onClick={() => setView(addMonths(view.year, view.month0, 1))}
            className="flex size-10 items-center justify-center rounded-md hover:bg-accent"
            aria-label="Следующий месяц"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-1 text-center text-[11px] text-muted-foreground">
              {w}
            </div>
          ))}
          {monthGrid(view.year, view.month0)
            .flat()
            .map((cell) => {
              const selected = isSelected(cell.iso);
              return (
                <button
                  key={cell.iso}
                  type="button"
                  onClick={() => onDay(cell.iso)}
                  aria-label={fmtDate(cell.iso)}
                  aria-pressed={selected}
                  aria-current={cell.iso === today ? "date" : undefined}
                  className={cn(
                    "h-10 rounded-md text-sm tabular-nums",
                    !cell.inMonth && "text-muted-foreground/40",
                    selected
                      ? "bg-primary font-semibold text-primary-foreground"
                      : isInRange?.(cell.iso)
                        ? "bg-primary/10"
                        : "hover:bg-accent",
                    cell.iso === today && !selected && "border border-primary/50",
                  )}
                >
                  {cell.day}
                </button>
              );
            })}
        </div>

        <p className="mt-2 text-center text-[11px] text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

/** Одна дата: тап по дню — выбор и закрытие. `value` — `yyyy-mm-dd` или пусто. */
export function DateCalendarDialog({
  value,
  onPick,
  onClose,
  hint = "Тапните дату",
}: {
  value: string;
  onPick: (iso: string) => void;
  onClose: () => void;
  hint?: React.ReactNode;
}): React.ReactElement {
  return (
    <CalendarDialog
      // Даты ещё нет — открываемся на текущем месяце (сегодня подсвечено).
      initialMonthOf={value || localISODate()}
      isSelected={(iso) => iso === value}
      onDay={onPick}
      onClose={onClose}
      hint={hint}
    />
  );
}
